import { Search } from "@mui/icons-material";
import { Box, Button, Grid, InputAdornment, Stack, TextField } from "@mui/material";
import dayjs from "dayjs";
import { useState } from "react";
import Modal from "../../../App/Components/Modal/Modal";
import { usePaginationState } from "../../../App/Components/Table/State/PaginationState";
import { useGetClientTextTemplatesQuery } from "../../../App/Queries/MessageQueries";
import { useDebouncedHandler } from "../../../App/State/Hooks/useDebouncedHandler";
import { IMessageHistory, ITextTemplate } from "../../../App/Typings/MessageText";
import { ITableColumn } from "../../../App/Typings/TableColumn";
import { MessageHistoryListItem } from "../../../MessageComponents/MessageDialog/Components/MessageHistory";
import MessageTemplateTableActions from "../../../MessageComponents/MessageTemplates/Components/MessageTemplateTableActions";
import { truncateMessageBody } from "../../../MessageComponents/MessageTemplates/EmailTemplateListDialog";
import { MessageTemplateTable } from "../../../MessageComponents/MessageTemplates/MessageTemplateTable";
import { DeleteTextTemplateModal } from "../Shared/DeleteTemplateModals";
import { DraftTextTemplateModal } from "./DraftTextTemplateModal";

export const DisplayTextTemplates = () => {
  const [selectedDeleteTemplate, setSelectedDeleteTemplate] = useState<ITextTemplate | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [action, setAction] = useState<"create" | "edit" | "duplicate">("create");
  const [selectedEditTemplate, setSelectedEditTemplate] = useState<ITextTemplate | null>(null);
  const [isDraftDialogOpen, setIsDraftDialogOpen] = useState(false);
  const {
    getters: { page, pageSize, search, sortBy, sortDirection },
    handlers: { handleChangePage, handleChangePageSize, handleSearchChange, handleRequestSort },
  } = usePaginationState({ sortBy: "modifiedDate", sortDirection: "desc" }, "text-templates");

  const { data: textTemplates = [], isLoading: isLoadingTextTemplates } =
    useGetClientTextTemplatesQuery(search);

  const [showPreview, setShowPreview] = useState(false);
  const [selectedPreviewTemplate, setSelectedPreviewTemplate] = useState<ITextTemplate | null>(null);
  const [previewTemplateView, setPreviewTemplateView] = useState<IMessageHistory | null>(null);

  const handleInputSearch = useDebouncedHandler((e: React.ChangeEvent<HTMLInputElement>) => {
    handleSearchChange(e);
  }, 200);

  const handleCreateTemplate = () => {
    setAction("create");
    setIsDraftDialogOpen(true);
  };

  const handleSelectEditTemplate = (template: ITextTemplate) => {
    const fullTemplate = textTemplates.find((t) => t.textTemplateId === template.textTemplateId);
    if (!fullTemplate) {
      return;
    }
    setAction("edit");
    setSelectedEditTemplate(fullTemplate);
    setIsDraftDialogOpen(true);
  };

  const handleSelectDuplicateTemplate = (template: ITextTemplate) => {
    const fullTemplate = {
      ...(textTemplates.find((t) => t.textTemplateId === template.textTemplateId) as ITextTemplate),
    };
    if (!fullTemplate) {
      return;
    }

    fullTemplate.name = `${fullTemplate.name} (1)`;
    fullTemplate.textTemplateId = "";

    setAction("duplicate");
    setSelectedEditTemplate(fullTemplate);
    setIsDraftDialogOpen(true);
  };

  const handleSelectDeleteTemplate = (template: ITextTemplate) => {
    setSelectedDeleteTemplate(template);
    setIsDeleteDialogOpen(true);
  };

  const handlePreview = (row: ITextTemplate) => {
    const originalRow = textTemplates?.find((template) => template.textTemplateId === row.textTemplateId);
    if (!originalRow) {
      setSelectedPreviewTemplate(null);
      setPreviewTemplateView(null);
      setShowPreview(false);
      return;
    }
    setPreviewTemplateView({
      messageId: "",
      messageTrackingId: "",
      scheduledTimeEastern: dayjs().utc().format("YYYY-MM-DDTHH:mm:ss"),
      messageDisplay: originalRow.body,
      messageState: "DELIVERED",
      userName: "",
      created: dayjs().utc(),
      candidateCount: 1,
      type: "DirectTextMessage",
      scheduledUtcTime: dayjs().utc(),
      dateSort: dayjs().utc(),
      messageType: "DirectTextMessage",
      isScheduled: false,
      groupName: "",
      errorMessage: "",
      hasBeenRead: false,
      userHasReplyNotification: false,
    });
    setSelectedPreviewTemplate(originalRow);
    setShowPreview(true);
  };

  const handleClosePreview = () => {
    setShowPreview(false);
    setTimeout(() => {
      setSelectedPreviewTemplate(null);
      setPreviewTemplateView(null);
    }, 300);
  };

  return (
    <Grid container>
      <Grid item xs={12}>
        <Stack
          sx={{ width: 1, mb: 2 }}
          direction={{ xs: "column", md: "row" }}
          justifyContent={{ xs: "flex-start", md: "space-between" }}
          alignItems="center"
          gap={{ xs: 2, md: 0 }}
        >
          <TextField
            data-test="text-templates-searchbar"
            label="Search"
            defaultValue={search}
            type="search"
            fullWidth
            sx={{ width: "40ch", maxWidth: "100%" }}
            onChange={handleInputSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ width: { xs: "40ch", md: "auto" }, maxWidth: "100%" }}>
            <Button onClick={handleCreateTemplate} variant="contained" fullWidth>
              Create Text Template
            </Button>
          </Box>
        </Stack>
      </Grid>
      <Grid item>
        <MessageTemplateTable
          name="text"
          columns={textTemplateColumns}
          loading={isLoadingTextTemplates}
          tableData={textTemplates.map((c) => ({
            ...truncateTextTemplateBody(c),
            actions: (
              <MessageTemplateTableActions
                id={c.textTemplateId}
                onDeleteClick={() => handleSelectDeleteTemplate(c)}
                onEditClick={() => handleSelectEditTemplate(c)}
                onPreviewClick={() => handlePreview(c)}
                onDuplicateClick={() => handleSelectDuplicateTemplate(c)}
              />
            ),
          }))}
          paginationOptions={{
            currentPage: page,
            rows: pageSize,
            totalCount: textTemplates.length,
            handleChangePage,
            handleChangeRowsPerPage: handleChangePageSize,
          }}
          customOrder={sortDirection}
          customOrderBy={sortBy}
          customRequestSort={(_, property) => handleRequestSort(property)}
        />
      </Grid>
      <DraftTextTemplateModal
        isDraftDialogOpen={isDraftDialogOpen}
        setIsDraftDialogOpen={setIsDraftDialogOpen}
        editTemplate={selectedEditTemplate}
        setEditTemplate={setSelectedEditTemplate}
        action={action}
      />
      <DeleteTextTemplateModal
        deleteTemplate={selectedDeleteTemplate}
        setDeleteTemplate={setSelectedDeleteTemplate}
        isDeleteDialogOpen={isDeleteDialogOpen}
        setIsDeleteDialogOpen={setIsDeleteDialogOpen}
      />
      <Modal
        title={`Preview Text Template: ${selectedPreviewTemplate ? selectedPreviewTemplate.name : ""}`}
        isOpen={showPreview}
        handleCloseModal={handleClosePreview}
        dataTestId={`preview-template${
          selectedPreviewTemplate ? "-" + selectedPreviewTemplate.textTemplateId : ""
        }`}
        content={
          previewTemplateView ? <MessageHistoryListItem msg={previewTemplateView} showDate={false} /> : <></>
        }
        footerContent={<></>}
        size="sm"
      />
    </Grid>
  );
};

const textTemplateColumns: ITableColumn[] = [
  { label: "Created", propName: "createdDate", order: 0 },
  { label: "Name", propName: "name", order: 1 },
  { label: "Body", propName: "body", order: 2 },
  { label: "Visibility", propName: "visibility", order: 3 },
  { label: "Modified", propName: "modifiedDate", order: 4 },
  { label: "Actions", propName: "actions", order: 5 },
];

const truncateTextTemplateBody = (template: ITextTemplate) => ({
  ...template,
  body: truncateMessageBody(template.body),
});
